import type {
  ChangedItem,
  DraftOperation,
  PreviewResponse,
} from "@/api/management";
import type { OrgTreeNode, ScopeVerdict } from "@/lib/org-structure-tree";

/**
 * Rascunho do Gerenciamento: as decisões ficam na tela até o "Salvar", que manda
 * a lista na ordem feita (`/scope-verdicts/batch`). O efeito de cada decisão —
 * cascata da estrutura, "todos os pendentes", contagens — é calculado pelo
 * servidor (`/management/preview`), o único que conhece a árvore inteira.
 */

export type { DraftOperation };
export type DraftNode = "org_structure" | "sscui" | "module_config";
export type VerdictCounts = Record<ScopeVerdict, number>;

export interface DraftState {
  /** Só o que difere do servidor: identidade → decisão do rascunho. */
  verdicts: Record<DraftNode, Map<string, ScopeVerdict>>;
  /** Quanto cada contagem muda em relação ao servidor, por aba. */
  deltas: Record<DraftNode, VerdictCounts>;
  /** Itens com decisão diferente da salva (o número do "Salvar"). */
  changes: number;
  /** Os itens mudados, com nome e rótulo, para as seções "não salvo". */
  items: ChangedItem[];
}

const zero = (): VerdictCounts => ({
  pending: 0,
  approved: 0,
  out_of_scope: 0,
});

export const EMPTY_DRAFT: DraftState = {
  verdicts: {
    org_structure: new Map(),
    sscui: new Map(),
    module_config: new Map(),
  },
  deltas: { org_structure: zero(), sscui: zero(), module_config: zero() },
  changes: 0,
  items: [],
};

/** Identidade de uma unidade: a mesma `nível|chave` que o servidor usa. */
export const orgIdentity = (node: Pick<OrgTreeNode, "levelCode" | "itemKey">) =>
  `${node.levelCode}|${node.itemKey}`;

/**
 * Estado do rascunho a partir da prévia do servidor. As operações que a prévia
 * ainda não contou (resposta em voo) entram já, pelos itens explícitos, para a
 * linha sair da lista no clique; a cascata delas chega com a próxima prévia.
 */
export function draftFromPreview(
  preview: PreviewResponse | undefined,
  pendingOps: DraftOperation[],
): DraftState {
  const state: DraftState = {
    verdicts: {
      org_structure: new Map(),
      sscui: new Map(),
      module_config: new Map(),
    },
    deltas: { org_structure: zero(), sscui: zero(), module_config: zero() },
    changes: preview?.changes ?? 0,
    items: preview?.items ?? [],
  };
  for (const item of preview?.codes ?? [])
    state.verdicts[item.node].set(item.code, item.verdict);
  for (const [node, counts] of Object.entries(preview?.deltas ?? {})) {
    if (node in state.deltas)
      state.deltas[node as DraftNode] = {
        ...zero(),
        ...counts,
      } as VerdictCounts;
  }
  for (const operation of pendingOps) {
    for (const item of operation.items) {
      const key =
        operation.node === "org_structure"
          ? `${item.item_type}|${item.item_key}`
          : item.item_key;
      state.verdicts[operation.node].set(key, operation.verdict);
    }
  }
  return state;
}

/** Aplica o rascunho numa floresta recém-montada (mapa; mutação local, nunca no cache). */
export function overlayOrgForest(
  roots: OrgTreeNode[],
  verdicts: ReadonlyMap<string, ScopeVerdict>,
): OrgTreeNode[] {
  if (verdicts.size === 0) return roots;
  const walk = (nodes: OrgTreeNode[]) => {
    for (const node of nodes) {
      const draft = verdicts.get(orgIdentity(node));
      if (draft) node.scopeVerdict = draft;
      walk(node.children);
    }
  };
  walk(roots);
  return roots;
}

/** Junta operações vizinhas iguais (mesmo nó e decisão, só itens explícitos). */
export function compactOperations(
  operations: DraftOperation[],
): DraftOperation[] {
  const out: DraftOperation[] = [];
  for (const operation of operations) {
    const last = out[out.length - 1];
    if (
      last &&
      !last.all_pending &&
      !operation.all_pending &&
      last.node === operation.node &&
      last.verdict === operation.verdict
    ) {
      out[out.length - 1] = {
        ...last,
        items: [...last.items, ...operation.items],
      };
    } else {
      out.push(operation);
    }
  }
  return out;
}
